/**
 * i18n context value + the React context object (#42, 2026-06-12).
 *
 * Split from `provider.tsx` so the `useTranslation` / `useLocale` hooks
 * (`hooks.ts`) and the `I18nProvider` component (`provider.tsx`) can each
 * fast-refresh independently — the same hook/component split the codebase
 * already uses for `auth-context.tsx` + `use-session.ts`.
 */

import { createContext } from 'react';
import { EN } from './catalog-en';
import type { Catalog, TranslationKey } from './catalog-en';

/** Supported locales. `en` is the default + fallback; `af` is lazy-loaded. */
export type Locale = 'en' | 'af';

export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'af'] as const;

export const DEFAULT_LOCALE: Locale = 'en';

export interface I18nContextValue {
  /** The active locale. */
  locale: Locale;
  /**
   * Translate a key. Always returns a string: if the active catalog is
   * missing the key (e.g. a not-yet-translated string in a lazy locale), it
   * falls back to the `en` base; if `en` is also missing it returns the key
   * itself so a wiring mistake is visible rather than rendering blank.
   */
  t: (key: TranslationKey) => string;
  /**
   * Switch the active locale. For a non-default locale this triggers the lazy
   * `import()` of its catalog; until the chunk resolves, `t` keeps returning
   * the previous (or `en`) strings — never a blank flash.
   */
  setLocale: (locale: Locale) => void;
  /** True while a newly-selected lazy catalog is still loading. */
  loading: boolean;
  /** The active catalog (mainly for tests / advanced consumers). */
  catalog: Catalog;
}

/**
 * The i18n context. Default value is a no-provider fallback: `en` strings, a
 * `t` that reads the `en` base directly (so a consumer rendered outside the
 * provider still shows real English text rather than crashing), and a no-op
 * `setLocale`. The real behaviour comes from `I18nProvider` in `provider.tsx`.
 */
export const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  t: (key) => EN[key] ?? key,
  setLocale: () => {},
  loading: false,
  catalog: EN,
});
